import { useState } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Eye, CheckCircle, Calendar, XCircle, Trash2 } from "lucide-react";
import { getTimeSlotDisplay } from "@/utils/calendarGenerator";

interface Appointment {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service_id: string;
  services: { name: string } | null;
  preferred_date: string;
  preferred_time_slot: string;
  address: string;
  city: string;
  zip_code: string;
  appointment_type: string;
  status: string;
  notes: string | null;
  confirmed_datetime: string | null;
  created_at: string;
}

interface AppointmentsTableProps {
  appointments: Appointment[];
  onViewDetails: (appointment: Appointment) => void;
  onConfirm: (id: string) => void;
  onReschedule: (appointment: Appointment) => void;
  onCancel: (id: string) => void;
  onComplete: (id: string) => void;
  onDelete: (id: string) => void;
}

const getStatusBadge = (status: string) => {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    pending: "secondary",
    confirmed: "default",
    completed: "outline",
    cancelled: "destructive",
    rescheduled: "secondary",
  };

  return (
    <Badge variant={variants[status] || "default"}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

export function AppointmentsTable({
  appointments,
  onViewDetails,
  onConfirm,
  onReschedule,
  onCancel,
  onComplete,
  onDelete,
}: AppointmentsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Service</TableHead>
            <TableHead>Date & Time</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                No appointments found
              </TableCell>
            </TableRow>
          ) : (
            appointments.map((appointment) => (
              <TableRow key={appointment.id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{appointment.customer_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {appointment.customer_phone}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{appointment.services?.name || "N/A"}</TableCell>
                <TableCell>
                  <div>
                    <div>{format(new Date(appointment.preferred_date), "MMM d, yyyy")}</div>
                    <div className="text-sm text-muted-foreground">
                      {getTimeSlotDisplay(appointment.preferred_time_slot)}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="capitalize">{appointment.appointment_type}</TableCell>
                <TableCell>{getStatusBadge(appointment.status)}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewDetails(appointment)}>
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      {appointment.status === "pending" && (
                        <DropdownMenuItem onClick={() => onConfirm(appointment.id)}>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Confirm
                        </DropdownMenuItem>
                      )}
                      {(appointment.status === "pending" || appointment.status === "confirmed") && (
                        <>
                          <DropdownMenuItem onClick={() => onReschedule(appointment)}>
                            <Calendar className="mr-2 h-4 w-4" />
                            Reschedule
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onCancel(appointment.id)}>
                            <XCircle className="mr-2 h-4 w-4" />
                            Cancel
                          </DropdownMenuItem>
                        </>
                      )}
                      {appointment.status === "confirmed" && (
                        <DropdownMenuItem onClick={() => onComplete(appointment.id)}>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Mark Complete
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => onDelete(appointment.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
